<div class="container-fluid p-4">
    <div class="row mb-3">
        <div class="col-12">
            <h2>{{ isEditMode() ? "Edit Bot Info" : "Create Bot Info" }}</h2>
        </div>
    </div>

    @if (!loading()) {
        <div class="row">
            <div class="col-12">
                <div class="row g-3">
                    <!-- Name -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Name *</mat-label>
                            <input
                                matInput
                                [ngModel]="name()"
                                (ngModelChange)="name.set($event)"
                                (blur)="markFieldTouched('name')"
                                name="name"
                                placeholder="Enter bot name"
                            />
                            <mat-error>{{ getFieldError("name") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Strategy Type -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Strategy Type *</mat-label>
                            <mat-select
                                [ngModel]="strategyType()"
                                (ngModelChange)="strategyType.set($event); onStrategyTypeChange()"
                                (blur)="markFieldTouched('strategyType')"
                                name="strategyType"
                                [disabled]="!isNew()"
                            >
                                @for (strategyType of strategyTypes; track strategyType) {
                                    <mat-option [value]="getStrategyTypeValue(strategyType)">
                                        {{ strategyType }}
                                    </mat-option>
                                }
                            </mat-select>
                            <mat-error>{{ getFieldError("strategyType") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Strategy Group -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Strategy Group *</mat-label>
                            <mat-select
                                [ngModel]="strategyGroup()"
                                (ngModelChange)="strategyGroup.set($event)"
                                (blur)="markFieldTouched('strategyGroup')"
                                name="strategyGroup"
                                [disabled]="!isNew()"
                            >
                                @for (strategyGroup of strategyGroups; track strategyGroup) {
                                    <mat-option [value]="getStrategyGroupValue(strategyGroup)">
                                        {{ strategyGroup }}
                                    </mat-option>
                                }
                            </mat-select>
                            <mat-error>{{ getFieldError("strategyGroup") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Martingale Strategy Options -->
                    @if (strategyType() === StrategyTypesEnum.Martingale && strategyOptions()) {
                        <div class="col-12">
                            <h5>Martingale Strategy Options</h5>
                            <app-martingale-strategy-options-edit
                                [StrategyOptions]="martingaleStrategyOptions()!"
                                (StrategyOptionsChange)="onStrategyOptionsChange($event)"
                                (IsValid)="onStrategyOptionsValidityChange($event)"
                            />
                        </div>
                    }

                    <!-- Custom Strategy Options -->
                    @if (strategyType() === StrategyTypesEnum.Custom && strategyOptions()) {
                        <div class="col-12">
                            <h5>Custom Strategy Options</h5>
                            <app-custom-strategy-options-edit
                                [StrategyOptions]="customStrategyOptions()!"
                                (StrategyOptionsChange)="onStrategyOptionsChange($event)"
                                (IsValid)="onStrategyOptionsValidityChange($event)"
                                [IsConfigurable]="true"
                            />
                        </div>
                    }

                    <!-- File -->
                    @if (strategyType() === StrategyTypesEnum.Custom) {
                        <div class="col-md-6">
                            <mat-form-field appearance="outline" class="w-100">
                                <mat-label>File *</mat-label>
                                <input
                                    matInput
                                    [ngModel]="file()"
                                    (ngModelChange)="file.set($event)"
                                    name="file"
                                    placeholder="File for custom strategy"
                                    type="text"
                                />
                                <mat-error>{{ getFieldError("file") }}</mat-error>
                            </mat-form-field>
                        </div>
                    }

                    <!-- Short Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Short Description *</mat-label>
                            <input
                                matInput
                                [ngModel]="shortDescription()"
                                (ngModelChange)="shortDescription.set($event)"
                                (blur)="markFieldTouched('shortDescription')"
                                name="shortDescription"
                                placeholder="Brief description"
                            />
                            <mat-error>{{ getFieldError("shortDescription") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Description *</mat-label>
                            <textarea
                                matInput
                                [ngModel]="description()"
                                (ngModelChange)="description.set($event)"
                                (blur)="markFieldTouched('description')"
                                name="description"
                                placeholder="Detailed description"
                                rows="4"
                            ></textarea>
                            <mat-error>{{ getFieldError("description") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Image URL -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Image URL</mat-label>
                            <input
                                matInput
                                [ngModel]="imageUrl()"
                                (ngModelChange)="imageUrl.set($event)"
                                name="imageUrl"
                                placeholder="https://example.com/image.jpg"
                            />
                        </mat-form-field>
                    </div>

                    <!-- File Upload -->
                    <div class="col-12">
                        <div class="mb-3">
                            <h6>Upload Image File</h6>
                            <div class="d-flex align-items-center gap-3 mb-3">
                                <input
                                    type="file"
                                    id="fileInput"
                                    class="form-control"
                                    style="max-width: 300px"
                                    accept="image/*"
                                    (change)="onFileSelected($event)"
                                    [disabled]="uploading()"
                                />
                                <button
                                    type="button"
                                    mat-raised-button
                                    color="primary"
                                    [disabled]="isUploadButtonDisabled()"
                                    (click)="uploadFile()"
                                >
                                    @if (!uploading()) {
                                        <mat-icon>cloud_upload</mat-icon>
                                    }
                                    @if (uploading()) {
                                        <mat-icon>hourglass_empty</mat-icon>
                                    }
                                    {{ uploading() ? "Uploading..." : "Upload File" }}
                                </button>
                            </div>

                            <!-- File Name Field - shown when file is selected -->
                            @if (selectedFile()) {
                                <div class="mb-3">
                                    <mat-form-field appearance="outline" class="w-100" style="max-width: 400px">
                                        <mat-label>File Name</mat-label>
                                        <input
                                            matInput
                                            [ngModel]="generatedFileName()"
                                            (ngModelChange)="onFileNameChange($event)"
                                            name="generatedFileName"
                                            placeholder="Generated file name"
                                            [disabled]="uploading()"
                                            matTooltip="Warning: If a file with the same name already exists on the server, it will be overridden"
                                            matTooltipPosition="above"
                                        />
                                        <mat-hint>File name will be used when uploading to server</mat-hint>
                                    </mat-form-field>
                                </div>
                            }
                        </div>
                    </div>

                    <!-- Price -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Price</mat-label>
                            <input
                                matInput
                                type="number"
                                [ngModel]="price()"
                                (ngModelChange)="price.set($event)"
                                name="price"
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                            />
                            <mat-error>{{ getFieldError("price") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Trading Symbols List -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Trading Symbols List *</mat-label>
                            <mat-select
                                [ngModel]="tradingSymbolsListId()"
                                (ngModelChange)="tradingSymbolsListId.set($event)"
                                (blur)="markFieldTouched('tradingSymbolsListId')"
                                name="tradingSymbolsListId"
                            >
                                @for (list of tradingSymbolsLists(); track list.id) {
                                    <mat-option [value]="list.id">
                                        {{ list.name }}
                                    </mat-option>
                                }
                            </mat-select>
                            <mat-error>{{ getFieldError("tradingSymbolsListId") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Risk Level -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Risk Level *</mat-label>
                            <mat-select
                                [ngModel]="riskLevel()"
                                (ngModelChange)="riskLevel.set($event)"
                                (blur)="markFieldTouched('riskLevel')"
                                name="riskLevel"
                            >
                                @for (level of riskLevelTypes; track level) {
                                    <mat-option [value]="getRiskLevelValue(level)">
                                        {{ level }}
                                    </mat-option>
                                }
                            </mat-select>
                            <mat-error>{{ getFieldError("riskLevel") }}</mat-error>
                        </mat-form-field>
                    </div>

                    <!-- Publisher -->
                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Publisher</mat-label>
                            <input
                                matInput
                                [ngModel]="publisher()"
                                (ngModelChange)="publisher.set($event)"
                                name="publisher"
                                placeholder="Publisher name"
                            />
                        </mat-form-field>
                    </div>

                    <!-- What To Expect Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>What To Expect Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="whatToExpectDescription()"
                                (ngModelChange)="whatToExpectDescription.set($event)"
                                name="whatToExpectDescription"
                                placeholder="What users can expect"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Risk Level Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Risk Level Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="riskLevelDescription()"
                                (ngModelChange)="riskLevelDescription.set($event)"
                                name="riskLevelDescription"
                                placeholder="Describe the risk level"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Performance Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Performance Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="performanceDescription()"
                                (ngModelChange)="performanceDescription.set($event)"
                                name="performanceDescription"
                                placeholder="Describe performance characteristics"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Entry Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Entry Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="entryDescription()"
                                (ngModelChange)="entryDescription.set($event)"
                                name="entryDescription"
                                placeholder="Describe entry strategy"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Exit Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Exit Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="exitDescription()"
                                (ngModelChange)="exitDescription.set($event)"
                                name="exitDescription"
                                placeholder="Describe exit strategy"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Recovery Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Recovery Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="recoveryDescription()"
                                (ngModelChange)="recoveryDescription.set($event)"
                                name="recoveryDescription"
                                placeholder="Describe recovery strategy"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Recommended Parameters Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Recommended Parameters Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="recomendedParametersDescription()"
                                (ngModelChange)="recomendedParametersDescription.set($event)"
                                name="recomendedParametersDescription"
                                placeholder="Describe recommended parameters"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <!-- Schedule Description -->
                    <div class="col-12">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Schedule Description</mat-label>
                            <textarea
                                matInput
                                [ngModel]="scheduleDescription()"
                                (ngModelChange)="scheduleDescription.set($event)"
                                name="scheduleDescription"
                                placeholder="Describe recommended schedule"
                                rows="3"
                            ></textarea>
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Annualized Return</mat-label>
                            <input
                                type="number"
                                step="0.01"
                                matInput
                                [ngModel]="annualizedReturn()"
                                (ngModelChange)="annualizedReturn.set($event)"
                                name="annualizedReturn"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Sharpe Ratio</mat-label>
                            <input
                                type="number"
                                step="0.01"
                                matInput
                                [ngModel]="sharpeRation()"
                                (ngModelChange)="sharpeRation.set($event)"
                                name="sharpeRation"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Max Drawdown</mat-label>
                            <input
                                type="number"
                                step="0.01"
                                matInput
                                [ngModel]="maxDropdown()"
                                (ngModelChange)="maxDropdown.set($event)"
                                name="maxDropdown"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Initial Investment</mat-label>
                            <input
                                type="number"
                                step="1"
                                matInput
                                [ngModel]="initialInvestment()"
                                (ngModelChange)="initialInvestment.set($event)"
                                name="initialInvestment"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Final Value</mat-label>
                            <input
                                type="number"
                                step="0.01"
                                matInput
                                [ngModel]="finalValue()"
                                (ngModelChange)="finalValue.set($event)"
                                name="finalValue"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Win Rate</mat-label>
                            <input
                                type="number"
                                step="0.01"
                                matInput
                                [ngModel]="winRate()"
                                (ngModelChange)="winRate.set($event)"
                                name="winRate"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Tested Range Start</mat-label>
                            <input
                                type="datetime-local"
                                matInput
                                [ngModel]="testedRangeStart()"
                                (ngModelChange)="testedRangeStart.set($event)"
                                (blur)="markFieldTouched('testedRangeStart')"
                                name="testedRangeStart"
                            />
                        </mat-form-field>
                    </div>

                    <div class="col-md-6">
                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Tested Range End</mat-label>
                            <input
                                type="datetime-local"
                                matInput
                                [ngModel]="testedRangeEnd()"
                                (ngModelChange)="testedRangeEnd.set($event)"
                                (blur)="markFieldTouched('testedRangeEnd')"
                                name="testedRangeEnd"
                            />
                        </mat-form-field>
                    </div>

                    <!-- Running Schedules -->
                    <div class="col-12">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h5>Running Schedules</h5>
                            <button type="button" mat-raised-button color="primary" (click)="addRunningSchedule()">
                                <mat-icon>add</mat-icon>
                                Add Schedule
                            </button>
                        </div>

                        @if (runningSchedules().length > 0) {
                            <div>
                                @for (schedule of runningSchedules(); track $index; let i = $index) {
                                    <div class="card mb-3">
                                        <div class="card-body">
                                            <div class="row g-3">
                                                <!-- Schedule Type -->
                                                <div class="col-md-4">
                                                    <mat-form-field appearance="outline" class="w-100">
                                                        <mat-label>Schedule Type *</mat-label>
                                                        <mat-select
                                                            [ngModel]="schedule.runningSchedule"
                                                            (ngModelChange)="
                                                                updateRunningSchedule(i, 'runningSchedule', $event)
                                                            "
                                                            [name]="'schedule_type_' + i"
                                                        >
                                                            @for (type of scheduleTypes; track type) {
                                                                <mat-option [value]="getScheduleTypeValue(type)">
                                                                    {{ type }}
                                                                </mat-option>
                                                            }
                                                        </mat-select>
                                                    </mat-form-field>
                                                </div>

                                                <!-- Buy Checkbox -->
                                                <div class="col-md-3 d-flex align-items-center">
                                                    <mat-checkbox
                                                        [ngModel]="schedule.buy"
                                                        (ngModelChange)="updateRunningSchedule(i, 'buy', $event)"
                                                        [name]="'schedule_buy_' + i"
                                                    >
                                                        Allow Buy
                                                    </mat-checkbox>
                                                </div>

                                                <!-- Sell Checkbox -->
                                                <div class="col-md-3 d-flex align-items-center">
                                                    <mat-checkbox
                                                        [ngModel]="schedule.sell"
                                                        (ngModelChange)="updateRunningSchedule(i, 'sell', $event)"
                                                        [name]="'schedule_sell_' + i"
                                                    >
                                                        Allow Sell
                                                    </mat-checkbox>
                                                </div>

                                                <!-- Remove Button -->
                                                <div class="col-md-2 d-flex align-items-center justify-content-end">
                                                    <button
                                                        type="button"
                                                        mat-icon-button
                                                        color="warn"
                                                        (click)="removeRunningSchedule(i)"
                                                        matTooltip="Remove Schedule"
                                                    >
                                                        <mat-icon>delete</mat-icon>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        }

                        @if (runningSchedules().length === 0) {
                            <div class="alert alert-info">
                                No running schedules configured. Click "Add Schedule" to add one.
                            </div>
                        }

                        <!-- Validation Error Messages -->
                        @if (getRunningSchedulesError() && runningSchedules().length > 0) {
                            <div class="alert alert-danger mt-2">
                                {{ getRunningSchedulesError() }}
                            </div>
                        }

                        <!-- Requirements Info -->
                        <div class="alert alert-warning">
                            <strong>Requirements:</strong>
                            <ul class="mb-0 mt-2">
                                <li>At least one schedule must be configured</li>
                                <li>At least one schedule must allow buying</li>
                                <li>At least one schedule must allow selling</li>
                            </ul>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="col-12 d-flex justify-content-end gap-2 mt-4">
                        <button type="button" mat-raised-button (click)="cancel()">Cancel</button>
                        <button
                            type="button"
                            mat-raised-button
                            color="primary"
                            [disabled]="isSaveButtonDisabled()"
                            (click)="save()"
                        >
                            {{ isEditMode() ? "Update" : "Create" }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    }

    @if (loading()) {
        <div class="text-center p-4">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    }
</div>
